import { createFileRoute, Link } from "@tanstack/react-router";
import clsx from "clsx";
import { AlertTriangle, Bug, CreditCard, Lightbulb, MessageCircle, Plus, Send } from "lucide-react";
import { useState } from "react";

import { useCreateTicketMutation } from "@/api/support/useCreateTicketMutation";
import { useMyTicketsQuery } from "@/api/support/useMyTicketsQuery";
import LoadingIcon from "@/components/feedback/LoadingIcon";
import { PanelSelect } from "@/components/form/PanelSelect";
import { BasicLayout } from "@/components/layout/BasicLayout";
import { useFloatingPanel } from "@/components/chat/useFloatingPanel";
import { usePanelDismiss } from "@/components/chat/usePanelDismiss";

const categoryLabels: Record<string, string> = {
  bug: "Usterka",
  payment: "Płatność",
  idea: "Pomysł",
};

const categoryIcons: Record<string, React.ReactNode> = {
  bug: <Bug className="size-3.5" />,
  payment: <CreditCard className="size-3.5" />,
  idea: <Lightbulb className="size-3.5" />,
};

const statusLabels: Record<string, string> = {
  new: "Nowe",
  in_progress: "W trakcie",
  resolved: "Rozwiązane",
  closed: "Zamknięte",
};

const statusColors: Record<string, string> = {
  new: "bg-accent/10 text-accent",
  in_progress: "bg-amber-500/10 text-amber-500",
  resolved: "bg-accent/10 text-accent",
  closed: "bg-surface-hover text-tertiary",
};

const categoryOptions = [
  { value: "bug" as const, label: "Usterka", icon: <Bug className="size-3.5" /> },
  { value: "payment" as const, label: "Płatność", icon: <CreditCard className="size-3.5" /> },
  { value: "idea" as const, label: "Pomysł", icon: <Lightbulb className="size-3.5" /> },
];

const priorityOptions = [
  { value: "low" as const, label: "Niski priorytet" },
  { value: "medium" as const, label: "Średni priorytet" },
  { value: "high" as const, label: "Wysoki priorytet", icon: <AlertTriangle className="size-3.5" /> },
  { value: "critical" as const, label: "Krytyczny", icon: <AlertTriangle className="size-3.5 text-red-500" /> },
];

export const Route = createFileRoute("/_auth/support/")({
  component: SupportPage,
});

function NewTicketPanel({
  onClose,
}: {
  onClose: () => void;
}) {
  const createTicket = useCreateTicketMutation();
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState<"bug" | "payment" | "idea">("bug");
  const [priority, setPriority] = useState<"low" | "medium" | "high" | "critical">("medium");
  const [content, setContent] = useState("");

  const { panelRef } = useFloatingPanel({});
  usePanelDismiss({ panelRef, onClose, enabled: !createTicket.isPending });

  const canSubmit = subject.trim() && content.trim() && !createTicket.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    createTicket.mutate(
      { subject, category, priority, content },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="absolute inset-x-0 bottom-0 pb-safe px-3 md:px-6 lg:px-8 z-20" ref={panelRef}>
      <div className="mx-auto max-w-2xl bg-panel backdrop-blur-3xl border border-accent/15 rounded-2xl shadow-xl shadow-accent/5 p-3 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-1 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-[1px] text-accent/70">
            Nowe zgłoszenie
          </span>
        </div>

        {/* Subject */}
        <div className="bg-surface/80 border border-border rounded-xl overflow-hidden focus-within:border-accent/40 transition-all mb-2">
          <input
            type="text"
            className="w-full px-3.5 py-2.5 text-sm text-primary bg-transparent placeholder:text-tertiary/50 outline-none"
            placeholder="Temat zgłoszenia..."
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            autoFocus
          />
        </div>

        {/* Category + Priority */}
        <div className="grid grid-cols-2 gap-2 mb-2">
          <PanelSelect
            options={categoryOptions}
            value={category}
            onChange={setCategory}
            disabled={createTicket.isPending}
          />
          <PanelSelect
            options={priorityOptions}
            value={priority}
            onChange={setPriority}
            disabled={createTicket.isPending}
          />
        </div>

        {/* Content */}
        <div className="bg-surface/80 border border-border rounded-xl overflow-hidden focus-within:border-accent/40 transition-all mb-3">
          <textarea
            className="w-full px-3.5 py-2.5 text-sm text-primary bg-transparent placeholder:text-tertiary/50 outline-none resize-none"
            rows={3}
            placeholder="Opisz szczegółowo problem..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && canSubmit) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        </div>

        {/* Submit */}
        <button
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent py-2.5 text-sm font-medium text-white shadow-glow hover:shadow-glow-strong disabled:opacity-40 disabled:shadow-none transition-all cursor-pointer"
        >
          {createTicket.isPending ? (
            <LoadingIcon className="size-4" />
          ) : (
            <Send className="size-4" />
          )}
          {createTicket.isPending ? "Wysyłanie..." : "Wyślij zgłoszenie"}
        </button>
      </div>
    </div>
  );
}

function SupportPage() {
  const { data, isPending } = useMyTicketsQuery();
  const [showForm, setShowForm] = useState(false);

  return (
    <BasicLayout title="Pomoc">
      <div className="relative h-full">
        <div className="max-w-[680px] mx-auto px-4 sm:px-6 py-8 sm:py-12 h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[1px] text-accent/70 mb-1">
                Pomoc
              </p>
              <h2 className="text-xl font-bold text-primary">
                Moje zgłoszenia
              </h2>
            </div>
            <button
              type="button"
              onClick={() => { setShowForm(!showForm); }}
              className={clsx(
                "inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-all cursor-pointer",
                showForm
                  ? "bg-surface-hover text-secondary"
                  : "bg-accent text-white shadow-glow hover:shadow-glow-strong",
              )}
            >
              {showForm ? (
                "Anuluj"
              ) : (
                <>
                  <Plus className="size-4" />
                  Nowe zgłoszenie
                </>
              )}
            </button>
          </div>

          {/* Ticket list */}
          {isPending ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-surface border border-border rounded-2xl p-5 animate-pulse">
                  <div className="h-4 bg-skeleton rounded w-2/3 mb-3" />
                  <div className="h-3 bg-skeleton rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : data?.tickets && data.tickets.length > 0 ? (
            <div className="space-y-2">
              {data.tickets.map(
                (ticket: {
                  id: string;
                  subject: string;
                  category: string;
                  status: string;
                  createdAt: string;
                }) => (
                  <Link
                    key={ticket.id}
                    to="/support/$ticketId"
                    params={{ ticketId: ticket.id }}
                    className="group block bg-surface border border-border rounded-2xl p-4 sm:p-5 hover:border-accent/20 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-primary text-sm group-hover:text-accent transition-colors truncate">
                          {ticket.subject}
                        </h3>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-tertiary">
                            {categoryIcons[ticket.category]}
                          </span>
                          <span className="text-[10px] text-tertiary">
                            {categoryLabels[ticket.category] ?? ticket.category}
                          </span>
                          <span className="text-tertiary text-[10px]">·</span>
                          <span className="text-[10px] text-tertiary tabular-nums">
                            {new Date(ticket.createdAt).toLocaleDateString("pl-PL", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                      </div>
                      <span
                        className={clsx(
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium shrink-0",
                          statusColors[ticket.status] ?? "",
                        )}
                      >
                        {statusLabels[ticket.status] ?? ticket.status}
                      </span>
                    </div>
                  </Link>
                ),
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="size-12 rounded-2xl bg-surface border border-border flex items-center justify-center mb-4">
                <MessageCircle className="size-6 text-tertiary" />
              </div>
              <p className="text-sm text-secondary mb-1">Brak zgłoszeń</p>
              <p className="text-xs text-tertiary">
                Kliknij &quot;Nowe zgłoszenie&quot; aby wysłać pierwsze.
              </p>
            </div>
          )}
        </div>

        {/* Floating new ticket panel */}
        {showForm && <NewTicketPanel onClose={() => setShowForm(false)} />}
      </div>
    </BasicLayout>
  );
}
